import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useDemo } from "../../../context/DemoContext";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDemoClock(isoString) {
  if (!isoString) return "—";
  const d    = new Date(isoString);
  const mo   = MONTHS[d.getMonth()];
  const day  = d.getDate();
  const yr   = d.getFullYear();
  let   h    = d.getHours();
  const m    = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${mo} ${day}, ${yr}  ${h}:${m} ${ampm}`;
}

export default function DemoTimeControls() {
  const history = useHistory();
  const {
    isDemoMode, demoState, loading,
    lastCycleResult, clearCycleResult,
    stepForward, stepBackward, seekTo, setStepMode,
  } = useDemo();

  const [seekOpen,    setSeekOpen]    = useState(false);
  const [seekVal,     setSeekVal]     = useState("");
  const [cycleFlash,  setCycleFlash]  = useState(null); // { text, color } shown briefly after cycle
  const flashTimer = useRef(null);

  // When a cycle result arrives, show a flash badge and maybe a toast
  useEffect(() => {
    if (!lastCycleResult) return;

    const { pending_count, actions_fired } = lastCycleResult;

    if (flashTimer.current) clearTimeout(flashTimer.current);

    if (pending_count > 0) {
      setCycleFlash({
        text:  `${pending_count} suggestion${pending_count > 1 ? "s" : ""} ready`,
        color: "#6f42c1",
        bg:    "#f0eaff",
      });

      toast(
        <span style={{ fontSize: 13 }}>
          <strong>{pending_count} new suggestion{pending_count > 1 ? "s" : ""}</strong>
          {" — "}
          <span
            style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
            onClick={() => history.push("/automation/suggestions")}
          >
            View &rarr;
          </span>
        </span>,
        {
          position:  "top-right",
          autoClose: 6000,
          style:     { background: "#f0eaff", color: "#3d2070", border: "1px solid #c9b0ff" },
          onClick:   () => history.push("/automation/suggestions"),
        }
      );
    } else if (actions_fired > 0) {
      setCycleFlash({ text: "Cycle complete", color: "#28a745", bg: "#d4edda" });
    } else {
      setCycleFlash({ text: "No new suggestions", color: "#6c757d", bg: "#f1f3f5" });
    }

    flashTimer.current = setTimeout(() => {
      setCycleFlash(null);
      clearCycleResult();
    }, 4000);

    return () => clearTimeout(flashTimer.current);
  }, [lastCycleResult, clearCycleResult, history]);

  if (!isDemoMode) return null;

  // When market replay is enabled, use the live-computed replay_clock (updates every 10s
  // via the /demo/state poll) so the header reflects the correct historical date/time
  // even if the cycle runner hasn't fired yet today.
  const isReplay = !!demoState?.is_replay_enabled;
  const clock    = (isReplay ? demoState?.replay_clock : null) || demoState?.demo_clock || null;
  const stepMode = demoState?.step_mode  || "hour";
  const cash     = demoState?.cash_balance != null
    ? `$${Number(demoState.cash_balance).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
    : null;

  const notInited = !clock;

  return (
    <div style={wrap}>
      {/* DEMO pill */}
      <span style={demoPill}>DEMO</span>

      {notInited ? (
        <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
          Not initialised — use <code style={{ fontSize: 11 }}>/make-video</code> to start
        </span>
      ) : (
        <>
          {/* Back button */}
          <button
            onClick={stepBackward}
            disabled={loading || stepMode === "cycle"}
            title={stepMode === "cycle" ? "Rewind disabled in 5M auto-play mode" : `Step back 1 ${stepMode}`}
            style={{ ...navBtn, opacity: (loading || stepMode === "cycle") ? 0.4 : 1 }}
          >
            &#9664;
          </button>

          {/* Clock / processing display */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => { if (!loading) { setSeekOpen(o => !o); setSeekVal(clock?.slice(0, 10) || ""); } }}
              style={{
                ...clockBtn,
                cursor: loading ? "not-allowed" : "pointer",
                background: loading ? "#e9ecef" : "#f8f9fa",
                color:      loading ? "#868e96" : "#2c3e50",
              }}
              title={loading ? "Processing cycle..." : "Click to jump to a date"}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={spinnerDot} />
                  <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.3px" }}>
                    Running cycle…
                  </span>
                </span>
              ) : (
                formatDemoClock(clock)
              )}
            </button>

            {/* Seek date picker popover */}
            {seekOpen && !loading && (
              <div style={seekBox}>
                <input
                  type="date"
                  value={seekVal}
                  onChange={e => setSeekVal(e.target.value)}
                  style={seekInput}
                />
                <button
                  style={seekGo}
                  onClick={() => { if (seekVal) seekTo(`${seekVal}T09:30:00`); setSeekOpen(false); }}
                >
                  Go
                </button>
                <button style={seekCancel} onClick={() => setSeekOpen(false)}>&#10005;</button>
              </div>
            )}
          </div>

          {/* Forward button */}
          <button
            data-testid="demo-step-forward"
            onClick={stepForward}
            disabled={loading}
            title={loading ? "Processing…" : `Step forward 1 ${stepMode}`}
            style={{ ...navBtn, opacity: loading ? 0.4 : 1 }}
          >
            &#9654;
          </button>

          {/* Post-cycle flash badge */}
          {cycleFlash && !loading && (
            <span
              data-testid="cycle-flash-badge"
              style={{
                ...flashBadge,
                color:      cycleFlash.color,
                background: cycleFlash.bg,
                border:     `1px solid ${cycleFlash.color}33`,
                cursor:     cycleFlash.text.includes("suggestion") ? "pointer" : "default",
              }}
              onClick={() => { if (cycleFlash.text.includes("suggestion")) history.push("/automation/suggestions"); }}
            >
              {cycleFlash.text}
            </span>
          )}

          {/* 5M / 1H / 1D toggle */}
          <div style={modeToggle}>
            {["cycle", "hour", "day"].map(m => (
              <button
                key={m}
                onClick={() => setStepMode(m)}
                disabled={loading}
                style={{
                  ...modeBtn,
                  background: stepMode === m ? "#6f42c1" : "transparent",
                  color:      stepMode === m ? "#fff"    : "#6f42c1",
                  fontWeight: stepMode === m ? 700       : 500,
                  opacity:    loading ? 0.5 : 1,
                }}
              >
                {m === "cycle" ? "5M" : m === "hour" ? "1H" : "1D"}
              </button>
            ))}
          </div>

          {/* Cash balance chip */}
          {cash && !loading && (
            <span style={cashChip} title="Virtual cash balance">{cash}</span>
          )}
        </>
      )}
    </div>
  );
}

/* ── Spinning dot animation ─────────────────────────────────────────────── */

const spinnerDot = {
  display:         "inline-block",
  width:           8,
  height:          8,
  borderRadius:    "50%",
  background:      "#6f42c1",
  animation:       "demoPulse 0.9s ease-in-out infinite",
};

// Inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("demo-pulse-style")) {
  const s = document.createElement("style");
  s.id = "demo-pulse-style";
  s.textContent = `
    @keyframes demoPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50%       { opacity: 0.3; transform: scale(0.6); }
    }
    @keyframes demoFlashIn {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(s);
}

/* ── Styles ─────────────────────────────────────────────────────────────── */

const wrap = {
  display: "flex", alignItems: "center", gap: 6,
  position: "relative",
};

const demoPill = {
  fontSize: 10, fontWeight: 800, letterSpacing: "0.7px",
  background: "#6f42c1", color: "#fff",
  padding: "2px 8px", borderRadius: 4,
  flexShrink: 0,
};

const navBtn = {
  background: "none", border: "1px solid #dee2e6", borderRadius: 5,
  padding: "4px 10px", cursor: "pointer", fontSize: 13, lineHeight: 1,
  color: "#495057", transition: "all 0.15s",
  flexShrink: 0,
};

const clockBtn = {
  border: "1px solid #dee2e6", borderRadius: 6,
  padding: "5px 16px", fontSize: 13, fontWeight: 700,
  fontFamily: "monospace", minWidth: 200, textAlign: "center",
  transition: "all 0.2s", lineHeight: "20px", height: 32,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const flashBadge = {
  fontSize: 11, fontWeight: 600,
  padding: "3px 10px", borderRadius: 12,
  animation: "demoFlashIn 0.25s ease",
  whiteSpace: "nowrap", flexShrink: 0,
};

const modeToggle = {
  display: "flex", border: "1px solid #6f42c1", borderRadius: 6, overflow: "hidden",
  flexShrink: 0,
};

const modeBtn = {
  border: "none", padding: "3px 9px", cursor: "pointer", fontSize: 11,
  fontWeight: 600, letterSpacing: "0.3px", transition: "all 0.15s",
};

const cashChip = {
  fontSize: 12, fontWeight: 600, color: "#155724",
  background: "#d4edda", padding: "3px 10px", borderRadius: 12,
  border: "1px solid #c3e6cb", flexShrink: 0,
};

const seekBox = {
  position: "absolute", top: "calc(100% + 8px)", left: "50%",
  transform: "translateX(-50%)",
  background: "#fff", border: "1px solid #dee2e6",
  borderRadius: 8, padding: "10px 12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  display: "flex", gap: 6, alignItems: "center",
  zIndex: 9999, whiteSpace: "nowrap",
};

const seekInput = {
  border: "1px solid #ccc", borderRadius: 5,
  padding: "5px 8px", fontSize: 13,
};

const seekGo = {
  background: "#6f42c1", color: "#fff", border: "none",
  borderRadius: 5, padding: "5px 14px", cursor: "pointer",
  fontSize: 13, fontWeight: 600,
};

const seekCancel = {
  background: "none", border: "none", cursor: "pointer",
  fontSize: 16, color: "#999", padding: "0 4px",
};
